import React, { useState } from 'react';

import { API } from 'constants';
import client from '../../../../services/template-api';

import { useFormState } from '../../../../hooks/useFormState';

import { generateUUID } from '../../../../helpers/generateUUID';
import { updateOrderKeys } from '../../../../helpers/insertItemAtSpecificPositionWithOrderUpdate';
import { normalizeMarkdown, prepareMarkdownOnLoad } from '../../../../helpers/normalizeMarkdown';

import { defaultErrorMessage } from '../../../../constants/errorMessages';

import Modal from '../../../../design-system/Modal/Modal';
import ErrorAlert from '../../../../design-system/ErrorAlert/ErrorAlert';
import ModalHeader from '../../../../design-system/ModalHeader/ModalHeader';
import NewRichTextArea from '../../../../design-system/NewRichTextArea/NewRichTextArea';
import { Button, Input } from '../../../../design-system';

const StepEditorModal = ({
    mode = 'create',
    currentStep,
    allSteps,
    setSteps,
    playbookTemplateId,
    onClose,
}) => {
    const { formData, handleInputChange } = useFormState(() => {
        if (mode === 'edit' && currentStep) {
            const { name, description } = currentStep;
            return { name, description: prepareMarkdownOnLoad(description) };
        }
        return { name: '', description: '' };
    });

    const [isLoading, setIsLoading] = useState(false);
    const [errorAlert, setErrorAlert] = useState(null);

    const allRequiredFieldsFilled = formData.name?.trim() && formData.description;
    const submitButtonState = isLoading
        ? 'loading'
        : allRequiredFieldsFilled
        ? 'default'
        : 'disabled';

    const getRequestBody = () => {
        const { name, description } = formData;

        const formattedStep = { name, description: normalizeMarkdown(description) };
        let newStepsArray = allSteps;

        if (mode === 'create') {
            const newStep = { ...formattedStep, order: (allSteps.length || 0) + 1 };
            newStepsArray = [...allSteps, newStep];
        }

        if (mode === 'edit') {
            newStepsArray = allSteps.map((item) =>
                item.temporaryId === currentStep.temporaryId ? { ...item, ...formattedStep } : item
            );
        }

        const formattedNewStepsArray = newStepsArray.map(({ temporaryId, ...step }) => step);
        return { steps: updateOrderKeys(formattedNewStepsArray) };
    };

    const handleSubmit = async () => {
        if (!allRequiredFieldsFilled) return;

        try {
            setIsLoading(true);

            const temporaryIdMap = new Map(
                allSteps.map((step) => [step.order + step.name, step.temporaryId || generateUUID()])
            );

            const requestBody = getRequestBody();

            const { data } = await client.patch(
                API.ROUTES.template.playbookTemplate + playbookTemplateId + '/',
                requestBody
            );

            setSteps(() => {
                const stepsFromResponse = data.default_version?.steps || [];
                const stepsWithTemporaryIds = stepsFromResponse.map((item) => {
                    const key = item.order + item.name;
                    const temporaryId = temporaryIdMap.get(key) || generateUUID();
                    return { ...item, temporaryId };
                });

                return stepsWithTemporaryIds.sort((a, b) => a.order - b.order);
            });

            onClose();
        } catch (e) {
            setIsLoading(false);
            setErrorAlert({ message: defaultErrorMessage, statusCode: e.response?.status });
        }
    };

    const title = {
        create: 'Add Step',
        edit: 'Edit Step',
    }[mode];

    const submitButtonText = {
        create: 'Add',
        edit: 'Save',
    }[mode];

    return (
        <Modal size="medium" onClose={onClose} resetPadding>
            <ModalHeader title={title} onClose={onClose} outlined />

            <div className="py-5 px-5 sm:px-6 md:px-8 flex flex-col gap-4">
                <Input
                    size="xs"
                    value={formData.name}
                    name="step_name"
                    label="Step Name"
                    isRequired
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    placeholder="Give this step a clear, unique name"
                />

                <NewRichTextArea
                    value={formData.description}
                    label="Step Description"
                    isRequired
                    setValue={(text) => handleInputChange('description', text)}
                    placeholder="Explain what happens in this step"
                    minHeight={161}
                    darkBorderColor
                />

                <div className="flex items-center justify-between gap-3">
                    <Button type="neutral" size="md" text="Cancel" onClick={onClose} />
                    <Button
                        type="secondary"
                        size="md"
                        text={submitButtonText}
                        state={submitButtonState}
                        onClick={handleSubmit}
                    />
                </div>
            </div>

            <ErrorAlert errorAlert={errorAlert} setErrorAlert={setErrorAlert} />
        </Modal>
    );
};

export default StepEditorModal;
